/**
 * Bilingual Route Mapping
 *
 * Maps English routes to their Spanish equivalents and vice versa.
 */

export const routeMap = {
  en: {
    home: "/",
    about: "/about",
    contact: "/contact",
    hireMe: "/hire-me",
    cto: "/cto",
    digitalMarketingSpecialist: "/digital-marketing-specialist",
    businessPartners: "/business-partners",
    googleCheck: "/google-check",
    privacyPolicy: "/privacy-policy",
    termsOfService: "/terms-of-service",
    accessibility: "/accessibility",
    agreements: "/agreements",
    agreementWebsitePlan: "/agreements/website-plan",
    agreementOnlineStorePlan: "/agreements/online-store-plan",
    agreementGoogleMapsOptimization: "/agreements/google-maps-optimization",
    agreementSocialMediaManagement: "/agreements/social-media-management",
    agreementLocalSeoPages: "/agreements/local-seo-pages",
    agreementMarketingSpecialistRetainer: "/agreements/marketing-specialist-retainer",
    agreementFixedScopeProject: "/agreements/fixed-scope-project",
    book: "/book",
    bookThankYou: "/book/thank-you",
    live: "/live",
    links: "/links",
    industries: "/industries",
    industriesRestaurants: "/industries/restaurants",
    industriesAttorneys: "/industries/attorneys",
    industriesEcommerce: "/industries/ecommerce",
    industriesFashionBrands: "/industries/fashion-brands",
    industriesSalons: "/industries/salons",
    industriesChildCare: "/industries/child-care",
    portfolio: "/portfolio",
    websiteAudit: "/website-audit",
    services: "/services",
    websiteDevelopment: "/services/website-development",
    localSeo: "/services/local-seo",
    technicalSeo: "/services/technical-seo",
    googleBusinessProfile: "/services/google-business-profile",
    socialMediaManagement: "/services/social-media-management",
    liveStreamProduction: "/services/live-stream-production",
    chatModeration: "/services/chat-moderation",
    remoteStreamProduction: "/services/remote-stream-production",
    growthMarketing: "/services/growth-marketing",
    digitalMarketing: "/services/digital-marketing",
    ecommerceWebsites: "/services/ecommerce-websites",
    shopifyWebsiteDevelopment: "/services/shopify-website-development",
    lawFirmWebsites: "/services/law-firm-websites",
    photography: "/services/photography",
    photographyFood: "/services/photography/food",
    photographyModel: "/services/photography/model",
    directory: "/directory",
    sampleReport: "/sample-report",
    blog: "/blog",
    blogCoordinatorVsRetainer: "/blog/marketing-coordinator-vs-retainer",
    blogLocalSeoCost: "/blog/local-seo-cost-dc-area",
    blogWebsiteAuditAnatomy: "/blog/anatomy-of-a-website-audit",
    blogTrafficNoLeads: "/blog/website-traffic-no-leads",
  },
  es: {
    home: "/es",
    about: "/es/sobre-nosotros",
    contact: "/es/contacto",
    hireMe: "/es/contratame",
    cto: "/es/cto",
    digitalMarketingSpecialist: "/es/especialista-en-marketing-digital",
    businessPartners: "/es/socios-de-negocio",
    googleCheck: "/es/revision-google",
    privacyPolicy: "/es/politica-de-privacidad",
    termsOfService: "/es/terminos-de-servicio",
    accessibility: "/es/accesibilidad",
    agreements: "/es/acuerdos",
    agreementWebsitePlan: "/es/acuerdos/plan-de-sitio-web",
    agreementOnlineStorePlan: "/es/acuerdos/plan-de-tienda-en-linea",
    agreementGoogleMapsOptimization: "/es/acuerdos/optimizacion-google-maps",
    agreementSocialMediaManagement: "/es/acuerdos/gestion-de-redes-sociales",
    agreementLocalSeoPages: "/es/acuerdos/paginas-de-seo-local",
    agreementMarketingSpecialistRetainer: "/es/acuerdos/contrato-de-especialista-en-marketing",
    agreementFixedScopeProject: "/es/acuerdos/proyecto-de-alcance-fijo",
    book: "/es/agendar",
    bookThankYou: "/es/agendar/gracias",
    live: "/es/en-vivo",
    links: "/es/enlaces",
    industries: "/es/industrias",
    industriesRestaurants: "/es/industrias/restaurantes",
    industriesAttorneys: "/es/industrias/abogados",
    industriesEcommerce: "/es/industrias/ecommerce",
    industriesFashionBrands: "/es/industrias/marcas-de-moda",
    industriesSalons: "/es/industrias/salones",
    industriesChildCare: "/es/industrias/cuidado-infantil",
    portfolio: "/es/portafolio",
    websiteAudit: "/es/auditoria-web",
    services: "/es/servicios",
    websiteDevelopment: "/es/servicios/desarrollo-web",
    localSeo: "/es/servicios/seo-local",
    technicalSeo: "/es/servicios/seo-tecnico",
    googleBusinessProfile: "/es/servicios/google-business-profile",
    socialMediaManagement: "/es/servicios/gestion-redes-sociales",
    liveStreamProduction: "/es/servicios/transmisiones-en-vivo",
    chatModeration: "/es/servicios/moderacion-de-chat",
    remoteStreamProduction: "/es/servicios/produccion-remota",
    growthMarketing: "/es/servicios/marketing-de-crecimiento",
    digitalMarketing: "/es/servicios/marketing-digital",
    ecommerceWebsites: "/es/servicios/sitios-web-ecommerce",
    shopifyWebsiteDevelopment: "/es/servicios/desarrollo-web-shopify",
    lawFirmWebsites: "/es/servicios/sitios-web-bufetes",
    photography: "/es/servicios/fotografia",
    photographyFood: "/es/servicios/fotografia/comida",
    photographyModel: "/es/servicios/fotografia/modelo",
    directory: "/es/directorio",
    sampleReport: "/es/reporte-de-muestra",
    blog: "/es/blog",
    blogCoordinatorVsRetainer: "/es/blog/coordinador-de-marketing-o-iguala",
    blogLocalSeoCost: "/es/blog/costo-seo-local-dc",
    blogWebsiteAuditAnatomy: "/es/blog/anatomia-de-una-auditoria-web",
    blogTrafficNoLeads: "/es/blog/trafico-sin-clientes",
  },
} as const;

export type Language = "en" | "es";
export type RouteKey = keyof typeof routeMap.en;

/**
 * Get the equivalent route in another language
 */
export const getAlternateRoute = (
  currentPath: string,
  targetLang: Language
): string => {
  // Normalize trailing slashes so "/cto/" still matches the map.
  const normalizedPath =
    currentPath.length > 1 ? currentPath.replace(/\/+$/, "") : currentPath;
  const isSpanish =
    normalizedPath === "/es" || normalizedPath.startsWith("/es/");
  const currentLang: Language = isSpanish ? "es" : "en";

  // Already in target language
  if (currentLang === targetLang) {
    return normalizedPath;
  }

  // Find the route key for current path
  const currentRoutes = routeMap[currentLang];
  const targetRoutes = routeMap[targetLang];

  for (const [key, path] of Object.entries(currentRoutes)) {
    if (path === normalizedPath) {
      return targetRoutes[key as RouteKey];
    }
  }

  // Fallback: simple prefix manipulation
  if (targetLang === "es") {
    return `/es${normalizedPath === "/" ? "" : normalizedPath}`;
  }

  // Remove /es prefix for English
  const withoutPrefix = normalizedPath.replace(/^\/es/, "");
  return withoutPrefix || "/";
};

/**
 * Get route by key and language
 */
export const getRoute = (key: RouteKey, lang: Language): string => {
  return routeMap[lang][key];
};
